'use client';

import { use, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';

import { activeLeadFormAtom } from '@core/Atoms/Leads/ActiveLeadForm.atom';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import Button from '@gds/Button/Button';
import useBreakpoint from '@gds/Pagination/Hooks/UseBreakpoint.hook';
import { Typography } from '@gds/Typography/Typography';
import {
  generateFloatingLeasingCtaTracking,
  generateFloatingLeasingLinkTracking,
} from '@shared-components/Leasing/LeasingPage.tracking';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import styles from './LeasingPage.module.css';

interface LeasingCtaProps {
  stringsPromise: Promise<Record<string, string>>;
  bodyTextType?: 'full' | 'short';
}

const LeasingCta = ({ stringsPromise, bodyTextType }: LeasingCtaProps) => {
  const strings = use(stringsPromise);
  const [isPhoneNumber, setShowPhoneNumber] = useState(false);
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);
  const {
    breakpoints: { isMobile },
  } = useBreakpoint();

  const handleClick = () => {
    const { event, context } = generateFloatingLeasingCtaTracking({
      searchParams,
      userId,
      formState,
      ctaType: 'link',
      ctaWording: 'Get in touch for leasing',
      ctaUrl: strings.messageUsUrl,
    });
    trackCustomEvent({ event, context });
    toggleLeadForm('leasing-online');
  };

  const handleLinkClick = () => {
    const { callStart, callSubmit } = generateFloatingLeasingLinkTracking({
      searchParams,
      userId,
      formState,
      listingId: 'uk_leasing_stock',
      leadType: 'leasing',
      ctaLocation: 'leasing_page',
      leadId: '',
      callNumber: strings.leasingCallUs,
    });

    if (isMobile) {
      window.location.href = `tel:${strings.leasingCallUs}`;
      trackCustomEvent({ event: callStart.event, context: callStart.context });
      trackCustomEvent({ event: callSubmit.event, context: callSubmit.context });
    } else {
      if (!isPhoneNumber) {
        setShowPhoneNumber(true);
        trackCustomEvent({ event: callStart.event, context: callStart.context });
      } else {
        window.location.href = `tel:${strings.leasingCallUs}`;
        trackCustomEvent({ event: callSubmit.event, context: callSubmit.context });
      }
    }
  };
  const toggleLeadForm = useSetAtom(activeLeadFormAtom);

  return (
    <div className={styles.ctaContainer}>
      {bodyTextType && (
        <Typography variant="body3" className={styles.bodyText}>
          {bodyTextType === 'full' ? strings.leasingTerms : strings.leasingShortTerms}
        </Typography>
      )}
      <div className={styles.ctasWrapper}>
        <Button
          className={`${styles.button} ${styles.primary}`}
          variant="contained"
          onClick={handleClick}
        >
          {strings.leasingMessageUs}
        </Button>
        <Button
          Component={Link}
          className={`${styles.button} ${styles.primary}`}
          variant="outlined"
          color="secondary"
          onClick={handleLinkClick}
          href={`tel:${strings.leasingCallUs}`}
        >
          {isMobile
            ? strings.leasingCallText
            : isPhoneNumber
              ? strings.leasingCallUs
              : strings.leasingCallText}
        </Button>
      </div>
    </div>
  );
};

export default LeasingCta;
